import React, { useMemo, useRef, useState } from "react";
import { useResizeObserver } from "usehooks-ts";
import { z } from "zod";

import { Text } from "@performyard/py-components";

import EmptyChart from "../../../../../../components/EmptyChart";
// components
import HorizontalPercentageBar, { PercentageBarSection } from "../../../../../../components/HorizontalPercentageBar";
import Table from "../../../../../../components/TableLite";
import Typography from "../../../../../../components/Text";

// utils
import { getColor } from "../../../../../../utils/getColor";

// styles
import css from "./CohortENPSDetailsTable.module.scss";

const eNPSDataSchema = z.object({
	/** the eNPS score for this cohort */
	eNPSScore: z.number().min(-100).max(100),
	/** ordered array with 3 counts: [detractors, passives, promoters] */
	percentages: z.array(z.number().int().min(0).max(100)).length(3),
	/** ordered array with 3 averages: [detractors, passives, promoters] */
	averages: z.array(z.number().min(0).max(10)).length(3),
	counts: z.array(z.number().int().min(0)).length(3).optional(),
});

const rowSchema = z.object({
	/** the name of the cohort */
	cohortName: z.string(),
	/** eNPS data for this cohort */
	eNPSData: eNPSDataSchema,
});

const enpsCategorySchema = z.object({
	/** the percentage of the category */
	percentage: z.number().int().min(0).max(100),
	/** the average of the category */
	average: z.number().min(0).max(10),
});

export type CohortENPSRow = z.infer<typeof rowSchema>;
type eNPSCategory = z.infer<typeof enpsCategorySchema>;

// fixed width columns
const COHORT_COLUMN_WIDTH = 184;
const ENPS_SCORE_COLUMN_WIDTH = 100;
// the width of the fixed width columns plus 64px worth of margins
// and padding. Used to calculate the width of the percentage bar.
const FIXED_WIDTHS = COHORT_COLUMN_WIDTH + ENPS_SCORE_COLUMN_WIDTH + 64;

// minimum pixels for the full text to be displayed
const MIN_FULL_TEXT_PIXELS = 40;
// minimum pixels for a section, used to replace any sections under this width
const REPLACEMENT_SECTION_PIXELS = 20;

type ENPSDetailsTableProps = {
	rows: CohortENPSRow[];
	isLoading: boolean;
};

const CohortENPSDetailsTable = ({ rows, isLoading }: ENPSDetailsTableProps) => {
	const percentageBarTableRef = useRef<HTMLDivElement>(null);
	const { width = 1 } = useResizeObserver({ ref: percentageBarTableRef });
	const [sortBy, setSortBy] = useState("cohortName");
	const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

	// map of index to text for each section of the percentage bar
	const enpsMap = {
		0: "Detractors",
		1: "Passives",
		2: "Promoters",
	};

	// indexes of all sections
	const enpsIndexes = Object.keys(enpsMap).map(Number);

	// map of index to color for each section of the percentage bar
	const colorMap = {
		0: getColor("red", 5),
		1: getColor("yellow", 5),
		2: getColor("green", 5),
	};

	const handleColumnClick = (column: string) => {
		if (column === sortBy) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortDirection("asc");
		}
		setSortBy(column);
	};

	// client side sorting of rows based on sort column and direction
	const sortedRows = useMemo(() => {
		const sortableRows = rows.slice(1);
		const iseNPSScoreColumn = sortBy === "eNPSScore";
		const sortedRows =
			sortableRows?.sort((rowA, rowB) => {
				// normalize
				const valueA = iseNPSScoreColumn ? rowA["eNPSData"]["eNPSScore"] : rowA[sortBy].trim().toLowerCase();
				const valueB = iseNPSScoreColumn ? rowB["eNPSData"]["eNPSScore"] : rowB[sortBy].trim().toLowerCase();
				// sort
				if (valueA === valueB) return 0;
				if (sortDirection === "asc") {
					return valueA > valueB ? 1 : -1;
				} else {
					return valueA < valueB ? 1 : -1;
				}
			}) || [];
		return [rows[0], ...sortedRows];
	}, [rows, sortBy, sortDirection]);

	/**
	 * Returns adjusted percentages and text for each section of the percentage bar.
	 * after any percentages under min are considered,
	 * the remaining percentages must be recalculated for the remaining sections.
	 */
	const getAdjustedPercentages = (sections: eNPSCategory[], percentageBarWidth: number): PercentageBarSection[] => {
		// the minimum percentage for full text converted from minimum pixels specified in design
		const minFullTextPercent = (MIN_FULL_TEXT_PIXELS / percentageBarWidth) * 100;
		// the minimum pecentage for width converted from pixels specified in design used to replace section percentages
		const minReplacementPercent = (REPLACEMENT_SECTION_PIXELS / percentageBarWidth) * 100;
		// indexes of all sections being replaced
		const replacementIndexes = enpsIndexes.filter(
			(i) => sections[i].percentage < minReplacementPercent && sections[i].percentage > 0
		);
		// total percentage of all sections being replaced
		const replacementPercentages = replacementIndexes.reduce((acc, i) => acc + sections[i].percentage, 0);
		// the total percentage of all sections we will add in
		const replacementTotal = replacementIndexes.length * minReplacementPercent;
		// the remaining percentage after replacement is considered
		const remainingPercentage = 100 - replacementTotal + replacementPercentages;

		return sections.map((section, index) => {
			// the percentage of this section
			const wholeNumberPercentage = Math.max(Math.round(section.percentage), 1);

			// whether we are shortenting the text for this section
			const shortenText = wholeNumberPercentage <= minFullTextPercent;

			// whatever text there is room to display
			const text = shortenText
				? `${wholeNumberPercentage}%`
				: `${wholeNumberPercentage}% (${parseFloat(section.average?.toFixed(1))})`;

			const inadequate = replacementIndexes.includes(index);

			return {
				percentage: inadequate ? minReplacementPercent : (section.percentage / 100) * remainingPercentage,
				text,
				tooltipText: [
					`${enpsMap[index]}: ${parseFloat(section.percentage.toFixed(0))}%`,
					`Average: ${parseFloat(section.average?.toFixed(1))}`,
				],
				color: colorMap[index],
				key: enpsMap[index].toLowerCase(),
			};
		});
	};

	return (
		<>
			{!isLoading && !rows.length ? (
				<div className={css.emptyContainer}>
					<EmptyChart type="cohortENPS" />
				</div>
			) : (
				<div ref={percentageBarTableRef} className={css.cardBody}>
					<Table
						theme="vJan23"
						activeSort={sortBy}
						activeSortDirection={sortDirection}
						onColumnClick={handleColumnClick}
						columns={[
							{
								id: "cohortName",
								label: "Cohort",
								width: `${COHORT_COLUMN_WIDTH}px`,
							},
							{
								id: "distribution",
								label: "Distribution of Ratings",
								width: "1fr",
								isSortable: false,
							},
							{
								id: "eNPSScore",
								label: "eNPS Score",
								width: `${ENPS_SCORE_COLUMN_WIDTH}px`,
							},
						]}
						rows={sortedRows.map((row, index) => ({
							id: `${row.cohortName}-${index}`,
							cohortName: (
								<Text
									wrap={false}
									size="caption"
									weight="regular"
									dangerouslySetClassName="py-text-truncate max-w-[184px]"
								>
									{row.cohortName}
								</Text>
							),
							eNPSScore: (
								<Typography size="paragraph-1" weight="regular" testID="enps-score">
									{row.eNPSData.eNPSScore}
								</Typography>
							),
							distribution: (
								<HorizontalPercentageBar
									// this makes the distribution column width proportionally scalable,
									// by basing on the width of the table ref, and subtracting the fixed widths
									fullWidth={width - FIXED_WIDTHS}
									hoverEffects={["grow"]}
									textColorOptions={[getColor("neutral", 0)]}
									sections={getAdjustedPercentages(
										enpsIndexes.map((index) => ({
											percentage: row.eNPSData.percentages[index],
											average: row.eNPSData.averages[index],
										})),
										width - FIXED_WIDTHS
									)}
								/>
							),
						}))}
					/>
				</div>
			)}
		</>
	);
};

export default CohortENPSDetailsTable;
